import { createFileRoute, Link } from '@tanstack/react-router'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { BRAND_MINT, formatCentsWithCurrency } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { getMyInvoicesApi, type EmployerInvoice } from '@/lib/billing'
import {
  LoaderIcon,
  FileTextIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  SearchIcon,
  XIcon,
  EyeIcon,
  CheckCircleIcon,
  ClockIcon,
  AlertTriangleIcon,
  XCircleIcon,
  RefreshCwIcon,
} from 'lucide-react'

const PAGE_SIZE = 20

const INVOICE_STATUS_STYLES: Record<string, { label: string; color: string }> = {
  pending: { label: 'Pending', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
  paid: { label: 'Paid', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
  overdue: { label: 'Overdue', color: 'bg-destructive/10 text-destructive' },
  cancelled: { label: 'Cancelled', color: 'bg-muted text-muted-foreground' },
  refunded: { label: 'Refunded', color: 'bg-purple-500/10 text-purple-600 dark:text-purple-400' },
}


function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  })
}

export const Route = createFileRoute('/dashboard/billing/invoices')({
  component: () => {
    const [invoices, setInvoices] = useState<EmployerInvoice[]>([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [loading, setLoading] = useState(true)
    const [refreshing, setRefreshing] = useState(false)
    const [statusFilter, setStatusFilter] = useState('')

    const fetchData = useCallback(async (targetPage: number, isRefresh: boolean) => {
      if (isRefresh) setRefreshing(true)
      else setLoading(true)
      try {
        const params: Record<string, string | number> = { page: targetPage, limit: PAGE_SIZE }
        if (statusFilter) params.status = statusFilter
        const res = await getMyInvoicesApi(params)
        setInvoices(res.data)
        setTotal(res.total)
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to load invoices')
      } finally {
        setLoading(false)
        setRefreshing(false)
      }
    }, [statusFilter])

    useEffect(() => {
      fetchData(page, false)
    }, [page, fetchData])

    useEffect(() => {
      setPage(1)
    }, [statusFilter])

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

    if (loading && invoices.length === 0) {
      return (
        <div className="mx-auto max-w-4xl">
          <div className="mb-8">
            <div className="flex items-start gap-3">
              <span aria-hidden="true" className="mt-0.5 shrink-0" style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }} />
              <h1 className="text-2xl font-semibold">Invoice history</h1>
            </div>
          </div>
          <div className="flex items-center justify-center rounded-xl border border-border bg-card py-16">
            <LoaderIcon className="size-5 animate-spin text-muted-foreground" />
            <p className="ml-2 text-sm text-muted-foreground">Loading invoices…</p>
          </div>
        </div>
      )
    }

    return (
      <div className="mx-auto max-w-4xl">
        <div className="mb-8">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <span aria-hidden="true" className="mt-0.5 shrink-0" style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }} />
              <div>
                <h1 className="text-2xl font-semibold">Invoice history</h1>
                <p className="mt-1 text-sm text-muted-foreground">View your past invoices and payment status.</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => fetchData(page, true)} disabled={refreshing} className="gap-1.5 shrink-0">
              <RefreshCwIcon className={`size-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Filter bar */}
        <div className="mb-6 flex gap-2">
          <div className="flex gap-1 rounded-lg border border-border bg-card p-1">
            {['', 'pending', 'paid', 'overdue'].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatusFilter(s)}
                className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                  statusFilter === s
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
          {statusFilter && (
            <Button variant="ghost" size="sm" onClick={() => setStatusFilter('')} className="gap-1 text-xs">
              <XIcon className="size-3" />Clear
            </Button>
          )}
        </div>

        {invoices.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-border bg-card py-16">
            <FileTextIcon className="size-10 text-muted-foreground/40" />
            <p className="text-sm font-medium text-foreground">No invoices found</p>
            <p className="text-sm text-muted-foreground">
              {statusFilter ? 'No invoices match the current filter.' : 'You don\'t have any invoices yet. Invoices are generated monthly.'}
            </p>
            {statusFilter && (
              <Button variant="outline" size="sm" onClick={() => setStatusFilter('')} className="gap-1.5">
                <XIcon className="size-4" />Clear filter
              </Button>
            )}
          </div>
        ) : (
          <>
            <p className="mb-4 text-xs text-muted-foreground">
              {total} invoice{total !== 1 ? 's' : ''}{page > 1 && ` — page ${page} of ${totalPages}`}
            </p>

            <div className="space-y-2">
              {invoices.map((inv) => {
                const statusInfo = INVOICE_STATUS_STYLES[inv.status] ?? { label: inv.status, color: 'bg-muted text-muted-foreground' }
                return (
                  <Link
                    key={inv.invoice_id}
                    to="/dashboard/billing/invoices/$invoiceId"
                    params={{ invoiceId: String(inv.invoice_id) }}
                    className="flex items-center gap-4 rounded-xl border border-border bg-card p-4 text-left transition-all hover:shadow-sm"
                  >
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <FileTextIcon className="size-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-medium text-foreground">{inv.invoice_number}</span>
                        <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${statusInfo.color}`}>
                          {inv.status === 'paid' && <CheckCircleIcon className="size-3" />}
                          {inv.status === 'overdue' && <AlertTriangleIcon className="size-3" />}
                          {inv.status === 'pending' && <ClockIcon className="size-3" />}
                          {inv.status === 'cancelled' && <XCircleIcon className="size-3" />}
                          {statusInfo.label}
                        </span>
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                        <span>{formatDate(inv.period_start)} — {formatDate(inv.period_end)}</span>
                        <span>{inv.total_active_users} users</span>
                        {inv.paid_at && (
                          <span>Paid {formatDate(inv.paid_at)}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold tabular-nums">{formatCentsWithCurrency(inv.amount_due_cents, inv.currency)}</span>
                      <EyeIcon className="size-4 shrink-0 text-muted-foreground" />
                    </div>
                  </Link>
                )
              })}
            </div>

            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-center gap-3">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="gap-1.5">
                  <ChevronLeftIcon className="size-4" />Previous
                </Button>
                <span className="text-xs text-muted-foreground">Page {page} of {totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="gap-1.5">
                  Next<ChevronRightIcon className="size-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    )
  },
})
